import type { Ref } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft, X } from 'lucide-react'
import { getWebFeatureProfile } from '../../lib/feature-flags'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { BrandLogo } from '../shared/brand-logo'
import {
  PRIMARY_RAIL_ITEMS,
  SECONDARY_RAIL_ITEMS,
  type RailItem,
  type RailItemKey,
} from './rail-items'

export type { RailItemKey } from './rail-items'

interface RailProps {
  userName?: string | null
  drawerMode?: boolean
  drawerOpen?: boolean
  navRef?: Ref<HTMLElement>
  onNavigate?: () => void
  onClose?: () => void
}

function initials(name?: string | null) {
  if (!name) return 'AW'
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'AW'
  )
}

export function getRailActiveKey(pathname: string): RailItemKey | null {
  if (pathname === '/') return 'home'
  if (pathname === '/doc' || pathname.startsWith('/doc/') || pathname.startsWith('/docs/')) {
    return 'knowledge'
  }
  if (pathname.startsWith('/search')) return 'search'
  if (pathname.startsWith('/graph')) return 'graph'
  if (pathname.startsWith('/sites')) return 'published'
  if (pathname.startsWith('/agents')) return 'agents'
  if (pathname.startsWith('/storage')) return getWebFeatureProfile().storageSpace ? 'files' : null
  if (pathname.startsWith('/billing')) return 'billing'
  if (pathname.startsWith('/settings') || pathname.startsWith('/secrets')) return 'settings'
  if (pathname.startsWith('/profile')) return 'profile'
  return null
}

function RailDestination({
  item,
  active,
  onNavigate,
}: {
  item: RailItem
  active: RailItemKey | null
  onNavigate?: () => void
}) {
  const Icon = item.icon
  return (
    <Link
      to={item.path}
      className={cn(
        'aw-rail-button',
        `aw-rail-button-${item.key}`,
        item.agent && 'agent',
        active === item.key && 'active',
      )}
      title={item.key === 'search' ? 'Search — ⌘K command palette' : item.label}
      aria-label={item.label}
      aria-current={active === item.key ? 'page' : undefined}
      aria-keyshortcuts={item.key === 'search' ? 'Control+K Meta+K' : undefined}
      data-rail-destination="true"
      onClick={onNavigate}
    >
      <Icon aria-hidden="true" />
      <span className="aw-rail-label">{item.label}</span>
      {item.shortcut && (
        <span className="aw-rail-shortcut" aria-hidden="true">
          {item.shortcut}
        </span>
      )}
    </Link>
  )
}

export function Rail({
  userName,
  drawerMode = false,
  drawerOpen = true,
  navRef,
  onNavigate,
  onClose,
}: RailProps) {
  const { railExpanded, toggleRailExpanded } = useAppStore()
  const active = getRailActiveKey(useLocation().pathname)
  const secondaryItems = getWebFeatureProfile().storageSpace
    ? SECONDARY_RAIL_ITEMS
    : SECONDARY_RAIL_ITEMS.filter((item) => item.key !== 'files')

  return (
    <nav
      id="global-navigation"
      ref={navRef}
      className="aw-rail"
      aria-label="Primary"
      aria-hidden={drawerMode ? !drawerOpen : undefined}
      inert={drawerMode && !drawerOpen ? true : undefined}
    >
      {drawerMode && drawerOpen && (
        <button
          type="button"
          className="aw-rail-drawer-close"
          aria-label="Close navigation"
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </button>
      )}
      <Link
        to="/"
        className="aw-rail-mark"
        title="AgentWiki"
        aria-label="AgentWiki Home"
        onClick={onNavigate}
      >
        <BrandLogo showWordmark={railExpanded || drawerMode} size="sm" />
      </Link>

      <div className="aw-rail-group" data-rail-group="primary">
        {PRIMARY_RAIL_ITEMS.map((item) => (
          <RailDestination key={item.key} item={item} active={active} onNavigate={onNavigate} />
        ))}
      </div>

      <div className="aw-rail-spacer" />

      <div className="aw-rail-group aw-rail-secondary" data-rail-group="secondary">
        {secondaryItems.map((item) => (
          <RailDestination key={item.key} item={item} active={active} onNavigate={onNavigate} />
        ))}
        <Link
          to="/profile"
          className={cn('aw-rail-user', active === 'profile' && 'active')}
          title={userName ?? 'Profile'}
          aria-label="Profile"
          aria-current={active === 'profile' ? 'page' : undefined}
          data-rail-destination="true"
          onClick={onNavigate}
        >
          <span className="aw-rail-avatar">{initials(userName)}</span>
          <span className="aw-rail-user-name">Profile</span>
        </Link>
      </div>

      {!drawerMode && (
        <button
          type="button"
          className="aw-rail-button aw-rail-toggle"
          onClick={toggleRailExpanded}
          title={railExpanded ? 'Collapse rail' : 'Expand rail'}
          aria-label={railExpanded ? 'Collapse rail' : 'Expand rail'}
        >
          <ChevronLeft aria-hidden="true" />
          <span className="aw-rail-label">{railExpanded ? 'Collapse' : 'Expand'}</span>
        </button>
      )}
    </nav>
  )
}
